// How this SPA signs in (unified-platform spec 2026-10-15 §4.2, R6). The
// manager API's public GET /api/auth/mode names the Keycloak authority and
// client in Keycloak mode. In legacy mode the build-time VITE_COGNITO_* apply.
// main.tsx loads it before rendering, so the synchronous helpers here are
// safe in interceptors and hooks.
export type AuthMode =
  | { mode: "keycloak"; authority: string; client_id: string; authz?: "projects" }
  | { mode: "legacy"; authority: null; client_id: null };

export const LEGACY_MODE: AuthMode = Object.freeze({ mode: "legacy", authority: null, client_id: null }) as AuthMode;
export const OIDC_CALLBACK_PATH = "/auth/callback";

export class AuthModeUnavailable extends Error {}

let current: AuthMode = LEGACY_MODE;

export function parseAuthMode(body: unknown): AuthMode {
  const b = body as { mode?: unknown; authority?: unknown; client_id?: unknown; authz?: unknown } | null;
  if (!b || b.mode !== "keycloak") return LEGACY_MODE;
  if (typeof b.authority !== "string" || !b.authority || typeof b.client_id !== "string" || !b.client_id) {
    return LEGACY_MODE;
  }
  return { mode: "keycloak", authority: b.authority.replace(/\/+$/, ""), client_id: b.client_id, ...(b.authz === "projects" ? { authz: "projects" } : {}) };
}

// 200: the answer. 401 or 404: an image older than the endpoint, so legacy.
// Anything else, or no answer: throw. A Keycloak deployment must never be
// taken for legacy, which would send Cognito tokens and loop.
export async function loadAuthMode(fetchFn: typeof fetch, apiOrigin: string): Promise<AuthMode> {
  let res: Response;
  try {
    res = await fetchFn(`${apiOrigin}/api/auth/mode`, { credentials: "omit" });
  } catch {
    throw new AuthModeUnavailable("auth mode: no answer");
  }
  if (res.status === 401 || res.status === 404) {
    current = LEGACY_MODE;
  } else if (res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      throw new AuthModeUnavailable("auth mode: response is not JSON");
    }
    current = parseAuthMode(body);
  } else {
    throw new AuthModeUnavailable(`auth mode: HTTP ${res.status}`);
  }
  return current;
}

export const getAuthMode = (): AuthMode => current;
export const isKeycloakMode = (): boolean => current.mode === "keycloak";

// Same-origin app paths only: browsers treat "/\" like "//", so reject backslashes too.
export function safeNext(next: unknown): string {
  if (typeof next !== "string" || !next.startsWith("/")) return "/";
  if (next.startsWith("//") || next.includes("\\")) return "/";
  if ([...next].some((c) => c.charCodeAt(0) < 0x20)) return "/";
  return next;
}

// The HashRouter path the user is on ("/config/aors" for "#/config/aors").
export function currentAppPath(): string {
  return safeNext(window.location.hash.replace(/^#/, "") || "/");
}

export function __setAuthModeForTests(mode: AuthMode): void {
  current = mode;
}
